import { SelectField, type SelectFieldProps } from '@/components';
import { type FieldValues, type Path, useFormContext } from 'react-hook-form';
import type { z } from 'zod';
import { useAccountingTypes } from '../../hooks';
import type { accountingType } from '../../models';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  fiscalYear: number | null;
  type?: z.infer<typeof accountingType> | null;
  allowAll?: boolean;
  allowClear?: boolean;
} & Pick<
  SelectFieldProps<T>,
  | 'shouldCoexistOnChange'
  | 'onChange'
  | 'width'
  | 'mode'
  | 'wrapperStyle'
  | 'required'
  | 'tagMaxWidth'
  | 'disabled'
>;

export const AccountingTypeSelectField = <T extends FieldValues>({
  name,
  label,
  fiscalYear,
  type,
  width = 160,
  allowAll = false,
  allowClear = false,
  shouldCoexistOnChange,
  onChange,
  mode,
  wrapperStyle,
  required,
  tagMaxWidth,
  disabled,
}: Props<T>) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  const accountingTypeOptions = useAccountingTypes({
    params: {
      ...(!allowAll &&
        fiscalYear && { referenceFiscalYear: fiscalYear.toString() }),
    },
    select: (data) =>
      data.flatMap((d) =>
        !type || d.type === type ? { value: d.id, label: d.name } : [],
      ),
    enabled: allowAll || !!fiscalYear || disabled,
  });

  return (
    <SelectField
      name={name}
      label={label}
      control={control}
      options={accountingTypeOptions.data}
      loading={accountingTypeOptions.isPending}
      errors={errors}
      allowClear={allowClear}
      disabled={type === 'general' || disabled}
      width={width}
      shouldCoexistOnChange={shouldCoexistOnChange}
      useNotInOption
      onChange={onChange}
      mode={mode}
      wrapperStyle={wrapperStyle}
      required={required}
      tagMaxWidth={tagMaxWidth}
    />
  );
};
